// src/pages/business/commission/CommissionApproval.tsx
// Commission approval management page

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, Loader2, Search,
  Filter, User, Package, CreditCard, Calendar, ChevronDown,
  AlertCircle, Info
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface CommissionRequest {
  id: string;
  customer_name: string;
  customer_phone?: string;
  product_name: string;
  commission_amount: number;
  sale_amount: number;
  commission_rate: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  notes?: string;
  created_at: string;
  updated_at?: string;
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pending', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.12)' },
  approved: { label: 'Approved', color: '#3B82F6', bgColor: 'rgba(59,130,246,0.12)' },
  rejected: { label: 'Rejected', color: '#EF4444', bgColor: 'rgba(239,68,68,0.12)' },
  paid: { label: 'Paid', color: '#10B981', bgColor: 'rgba(16,185,129,0.12)' },
};

const CommissionApproval: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [requests, setRequests] = useState<CommissionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | CommissionRequest['status']>('pending');
  const [actionId, setActionId] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, [businessId]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/commissions`);
      setRequests(res.data.commissions || []);
    } catch (error: any) {
      console.error('Failed to fetch commissions:', error);
      toastError('Failed to load commission requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchRequests();
  };

  const handleApprove = async (requestId: string) => {
    setActionId(requestId);
    try {
      await apiClient.patch(`/businesses/${businessId}/commissions/${requestId}/approve`);
      toastSuccess('Commission request approved');
      fetchRequests();
    } catch (error: any) {
      toastError('Failed to approve request');
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to reject this commission request?')) return;
    setActionId(requestId);
    try {
      await apiClient.patch(`/businesses/${businessId}/commissions/${requestId}/reject`);
      toastSuccess('Commission request rejected');
      fetchRequests();
    } catch (error: any) {
      toastError('Failed to reject request');
    } finally {
      setActionId(null);
    }
  };

  const filteredRequests = requests.filter((r) => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = searchQuery === '' || 
      r.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.product_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    total: requests.length,
    totalCommission: requests
      .filter(r => r.status === 'approved' || r.status === 'paid')
      .reduce((sum, r) => sum + r.commission_amount, 0),
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Commission Approvals
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Commission Approvals
        </h1>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{stats.total}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Total</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{stats.pending}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Pending</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>{stats.approved}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Approved</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#10B981' }}>
              KES {stats.totalCommission.toLocaleString()}
            </p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Total Commission</p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
            <input
              type="text"
              placeholder="Search by customer or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setFilter(filter === 'all' ? 'pending' : filter === 'pending' ? 'approved' : filter === 'approved' ? 'rejected' : filter === 'rejected' ? 'paid' : 'all')}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm font-semibold text-light-text dark:text-dark-text"
            >
              <Filter size={16} />
              {filter === 'all' ? 'All' : statusConfig[filter]?.label}
              <ChevronDown size={16} />
            </button>
          </div>
        </div>

        {/* Requests List */}
        {filteredRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <CreditCard size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No Commission Requests
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery || filter !== 'all'
                ? 'Try adjusting your search or filter'
                : 'Commission requests will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map((request) => {
              const status = statusConfig[request.status];
              const isProcessing = actionId === request.id;

              return (
                <div
                  key={request.id}
                  className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border shadow-sm"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                        <User size={20} style={{ color: '#4F46E5' }} />
                      </div>
                      <div>
                        <p className="font-bold text-light-text dark:text-dark-text">{request.customer_name}</p>
                        {request.customer_phone && (
                          <p className="text-xs text-light-subtle dark:text-dark-subtle">{request.customer_phone}</p>
                        )}
                      </div>
                    </div>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: status.bgColor, color: status.color }}
                    >
                      {status.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Package size={14} className="text-light-subtle dark:text-dark-subtle" />
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Product</p>
                      </div>
                      <p className="font-medium text-sm text-light-text dark:text-dark-text">{request.product_name}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard size={14} className="text-light-subtle dark:text-dark-subtle" />
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Sale Amount</p>
                      </div>
                      <p className="font-medium text-sm text-light-text dark:text-dark-text">
                        KES {request.sale_amount.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Info size={14} className="text-light-subtle dark:text-dark-subtle" />
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Commission Rate</p>
                      </div>
                      <p className="font-medium text-sm text-light-text dark:text-dark-text">{request.commission_rate}%</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard size={14} className="text-light-subtle dark:text-dark-subtle" />
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Commission</p>
                      </div>
                      <p className="font-bold text-sm" style={{ color: '#10B981' }}>
                        KES {request.commission_amount.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-light-border dark:border-dark-border">
                    <p className="text-xs text-light-subtle dark:text-dark-subtle flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(request.created_at)}
                    </p>
                    {request.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReject(request.id)}
                          disabled={isProcessing}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                        >
                          {isProcessing ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                          Reject
                        </button>
                        <button
                          onClick={() => handleApprove(request.id)}
                          disabled={isProcessing}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                          style={{ backgroundColor: '#3B82F6' }}
                        >
                          {isProcessing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                          Approve
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50"
        style={{ backgroundColor: '#4F46E5' }}
      >
        <Loader2 size={24} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default CommissionApproval;